import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { cn } from '@/lib/utils';
import { CAMPAIGNS_PAGE_SIZE_OPTIONS } from '../../constants/campaigns.constants';

const PAGE_SIZE_OPTIONS = CAMPAIGNS_PAGE_SIZE_OPTIONS;

export interface CampaignsPaginationProps {
  pageNo: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPageChange: (pageNo: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  className?: string;
}

export const CampaignsPagination = ({
  pageNo,
  pageSize,
  totalCount,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  onPageChange,
  onPageSizeChange,
  className,
}: CampaignsPaginationProps) => {
  const start = totalCount === 0 ? 0 : (pageNo - 1) * pageSize + 1;
  const end = Math.min(pageNo * pageSize, totalCount);
  const countLabel =
    totalCount === 0
      ? 'Showing 0 of 0'
      : `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${totalCount.toLocaleString()}`;

  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-center justify-between gap-4 border-t pt-4 text-sm text-muted-foreground',
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap">Rows per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]" aria-label="Rows per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="top">
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p>{countLabel}</p>
      </div>

      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-muted-foreground"
          onClick={() => onPageChange(pageNo - 1)}
          disabled={!hasPreviousPage || totalPages === 0}
        >
          <CaretLeft className="mr-1 h-4 w-4" aria-hidden />
          Previous
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-muted-foreground"
          onClick={() => onPageChange(pageNo + 1)}
          disabled={!hasNextPage || totalPages === 0}
        >
          Next
          <CaretRight className="ml-1 h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
};
